import { Type, type Static } from '@sinclair/typebox';
import { SeoKeywordSource } from '../translation_namespace/seo_content_namespace';
import { SeoContentType, SeoSearchIntent } from './seo-brief.schema';

/**
 * Mot-clé candidat produit par `discoverKeywords` (plan §4.3 job 1), avant
 * insertion en `seo_keyword`. Le score est relatif au lot (0-100).
 */
export const SeoSerpSnapshotSchema = Type.Object({
  fetched_at: Type.String(),
  /** Domaines des 10 premiers résultats, dans l'ordre. */
  top_domains: Type.Array(Type.String()),
  /** URLs des 10 premiers résultats. */
  top_urls: Type.Array(Type.String()),
  people_also_ask: Type.Array(Type.String()),
  related_searches: Type.Array(Type.String()),
  has_featured_snippet: Type.Optional(Type.Boolean()),
  /**
   * Résultats organiques tels que lus (titre, extrait) et extraits des « Autres questions posées »
   * (2026-10-21) : le brief d'un sujet validé réutilise cette SERP au lieu de la relire
   * (`serpFromSnapshot`), tant qu'elle est assez fraîche. Absents sur les snapshots antérieurs.
   */
  organic: Type.Optional(
    Type.Array(
      Type.Object({
        position: Type.Integer({ minimum: 1 }),
        link: Type.String(),
        title: Type.String(),
        snippet: Type.Optional(Type.String()),
      }),
    ),
  ),
  paa_snippets: Type.Optional(
    Type.Array(Type.Object({ question: Type.String(), snippet: Type.Optional(Type.String()) })),
  ),
});
export type SeoSerpSnapshot = Static<typeof SeoSerpSnapshotSchema>;

export const SeoKeywordCandidateSchema = Type.Object({
  keyword: Type.String({ minLength: 2 }),
  /** Forme normalisée (minuscules, sans accents, espaces simples) : clé d'unicité. */
  normalized: Type.String(),
  intent: Type.Enum(SeoSearchIntent),
  /** Regroupement par sujet (une page par cluster). */
  cluster_key: Type.String(),
  content_type: Type.Enum(SeoContentType),
  /** Zone visée quand le mot-clé est local. */
  area: Type.Optional(Type.String()),
  volume: Type.Optional(Type.Integer({ minimum: 0 })),
  cpc: Type.Optional(Type.Number({ minimum: 0 })),
  /** Difficulté 0-100 si connue (Haloscan). */
  difficulty: Type.Optional(Type.Number({ minimum: 0, maximum: 100 })),
  sources: Type.Array(Type.Enum(SeoKeywordSource)),
  /** Position GSC actuelle, si le site ranke déjà. */
  gsc_position: Type.Optional(Type.Number()),
  gsc_impressions: Type.Optional(Type.Integer({ minimum: 0 })),
  serp: Type.Optional(SeoSerpSnapshotSchema),
  score: Type.Number({ minimum: 0, maximum: 100 }),
  /** Pourquoi ce sujet mérite une page (affiché au pro / à l'admin). */
  why_new: Type.String(),
  /** URL de la page qui couvre déjà l'intention, le cas échéant. */
  covered_by_url: Type.Optional(Type.String()),
  /**
   * Page du site déjà positionnée (GSC) sur la requête quand le sujet est retenu malgré tout
   * (accueil, ou page loin dans les résultats) : le brief lui impose un lien, l'article la soutient.
   */
  support_url: Type.Optional(Type.String()),
});
export type SeoKeywordCandidate = Static<typeof SeoKeywordCandidateSchema>;
